import {
  type CreateLeadDto,
  type Lead,
  type LeadAttachmentKind,
  LeadsApi,
  LeadSource,
  LeadStatus,
  type UpdateLeadDto,
} from '@up2stay/up2promo-api-client';

import type { LeadAttachmentKindValue } from '@/core/constants/lead-attachment-kind';
import { getApiBusinessErrorCode } from '@/core/utils/api-error.utils';

export interface LeadIncomingAttachment {
  fileId: string;
  kind: LeadAttachmentKindValue;
  mimeType: string;
  title: string;
}

export interface LeadIncomingData {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  jobTitle?: string;
  address?: string;
  message?: string;
  source: LeadSource;
  profileId?: string | null;
  workspaceId?: string | null;
  status?: LeadStatus;
  attachments?: LeadIncomingAttachment[];
  eventIds?: string[];
}

function mergeMessages(
  existing: string | null | undefined,
  incoming: string | null | undefined,
): string | null {
  const current = existing?.trim() ?? '';
  const next = incoming?.trim() ?? '';
  if (!next) return current || null;
  if (!current) return next;
  if (current.includes(next)) return current;
  return `${current}\n\n---\n${next}`;
}

async function addAttachments(
  workspaceId: string,
  leadId: string,
  attachments: LeadIncomingAttachment[] | undefined,
): Promise<void> {
  if (!attachments?.length) return;
  for (const attachment of attachments) {
    await LeadsApi.addAttachment({
      path: { workspaceId, id: leadId },
      body: {
        ...attachment,
        kind: attachment.kind as LeadAttachmentKind,
      },
    });
  }
}

export class LeadDuplicateConflictError extends Error {
  readonly leads: Lead[];
  readonly incoming: LeadIncomingData | null;

  constructor(leads: Lead[], incoming: LeadIncomingData | null = null) {
    super('leadDuplicate');
    this.name = 'LeadDuplicateConflictError';
    this.leads = leads;
    this.incoming = incoming;
  }
}

function readDuplicateErrorPayload(err: unknown): {
  existingLeads?: unknown;
  incoming?: unknown;
} {
  const boxed = err as { response?: { data?: unknown }; error?: unknown };
  const candidates = [boxed.response?.data, boxed.error];
  for (const candidate of candidates) {
    if (typeof candidate !== 'object' || candidate === null) continue;
    const obj = candidate as Record<string, unknown>;
    const nested =
      typeof obj.message === 'object' && obj.message !== null
        ? (obj.message as Record<string, unknown>)
        : null;
    const existingLeads = obj.existingLeads ?? nested?.existingLeads;
    const incoming = obj.incoming ?? nested?.incoming;
    if (existingLeads !== undefined || incoming !== undefined) {
      return { existingLeads, incoming };
    }
  }
  return {};
}

function parseDuplicateLeadsFromError(err: unknown): Lead[] {
  const payload = readDuplicateErrorPayload(err);
  if (!Array.isArray(payload.existingLeads)) {
    return [];
  }
  return payload.existingLeads.filter((row): row is Lead => {
    return (
      typeof row === 'object' &&
      row !== null &&
      'id' in row &&
      typeof (row as { id?: unknown }).id === 'string'
    );
  });
}

function parseIncomingFromError(err: unknown): LeadIncomingData | null {
  const payload = readDuplicateErrorPayload(err);
  if (typeof payload.incoming !== 'object' || payload.incoming === null) {
    return null;
  }
  const incoming = payload.incoming as Record<string, unknown>;
  const firstName =
    typeof incoming.firstName === 'string' ? incoming.firstName.trim() : '';
  if (!firstName) return null;

  const readOptionalString = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  };

  return {
    firstName,
    lastName: readOptionalString(incoming.lastName),
    email: readOptionalString(incoming.email),
    phone: readOptionalString(incoming.phone),
    companyName: readOptionalString(incoming.companyName),
    jobTitle: readOptionalString(incoming.jobTitle),
    source: LeadSource.BUSINESS_CARD,
  };
}

export function getLeadDuplicateConflict(err: unknown): {
  leads: Lead[];
  incoming: LeadIncomingData | null;
} | null {
  if (getApiBusinessErrorCode(err) !== 'leadDuplicate') {
    return null;
  }
  return {
    leads: parseDuplicateLeadsFromError(err),
    incoming: parseIncomingFromError(err),
  };
}

export async function createLeadDirect(
  incoming: LeadIncomingData,
  options?: { allowDuplicate?: boolean },
): Promise<string | undefined> {
  const workspaceId = incoming.workspaceId?.trim();
  if (!workspaceId) {
    throw new Error('workspaceId required');
  }
  try {
    const response = await LeadsApi.create({
      path: { workspaceId },
      body: {
        ...(incoming.profileId ? { profileId: incoming.profileId } : {}),
        workspaceId,
        firstName: incoming.firstName.trim(),
        ...(incoming.lastName?.trim() && {
          lastName: incoming.lastName.trim(),
        }),
        ...(incoming.email?.trim() && { email: incoming.email.trim() }),
        ...(incoming.phone?.trim() && { phone: incoming.phone.trim() }),
        ...(incoming.companyName?.trim() && {
          companyName: incoming.companyName.trim(),
        }),
        ...(incoming.jobTitle?.trim() && {
          jobTitle: incoming.jobTitle.trim(),
        }),
        ...(incoming.address?.trim() && { address: incoming.address.trim() }),
        ...(incoming.message?.trim() && { message: incoming.message.trim() }),
        ...(incoming.attachments?.length
          ? {
              attachments: incoming.attachments.map((attachment) => ({
                ...attachment,
                kind: attachment.kind as LeadAttachmentKind,
              })),
            }
          : {}),
        status: incoming.status ?? LeadStatus.NEW,
        source: incoming.source,
        ...(incoming.eventIds?.length ? { eventIds: incoming.eventIds } : {}),
        ...(options?.allowDuplicate ? { allowDuplicate: true } : {}),
      } satisfies CreateLeadDto,
    });
    return response.data?.id;
  } catch (error) {
    if (getApiBusinessErrorCode(error) === 'leadDuplicate') {
      throw new LeadDuplicateConflictError(
        parseDuplicateLeadsFromError(error),
        parseIncomingFromError(error),
      );
    }
    throw error;
  }
}

export async function replaceExistingLead(
  leadId: string,
  incoming: LeadIncomingData,
  existing?: Lead,
): Promise<string> {
  const workspaceId =
    incoming.workspaceId?.trim() || existing?.workspaceId?.trim();
  if (!workspaceId) {
    throw new Error('workspaceId required');
  }
  await LeadsApi.update({
    path: { workspaceId, id: leadId },
    body: {
      firstName: incoming.firstName.trim(),
      lastName: incoming.lastName?.trim() || undefined,
      email: incoming.email?.trim() || null,
      phone: incoming.phone?.trim() || null,
      companyName: incoming.companyName?.trim() || null,
      jobTitle: incoming.jobTitle?.trim() || null,
      address: incoming.address?.trim() || null,
      message: incoming.message?.trim() || null,
    },
  });
  await addAttachments(workspaceId, leadId, incoming.attachments);
  return leadId;
}

export async function supplementExistingLead(
  leadId: string,
  existing: Lead,
  incoming: LeadIncomingData,
): Promise<string> {
  const updateBody: {
    firstName?: string;
    lastName?: string;
    email?: string | null;
    phone?: string | null;
    companyName?: string | null;
    jobTitle?: string | null;
    address?: string | null;
    message?: string | null;
  } = {};

  if (incoming.firstName.trim() && !existing.firstName?.trim()) {
    updateBody.firstName = incoming.firstName.trim();
  }
  if (incoming.lastName?.trim() && !existing.lastName?.trim()) {
    updateBody.lastName = incoming.lastName.trim();
  }
  if (incoming.email?.trim() && !existing.email?.trim()) {
    updateBody.email = incoming.email.trim();
  }
  if (incoming.phone?.trim() && !existing.phone?.trim()) {
    updateBody.phone = incoming.phone.trim();
  }
  if (incoming.companyName?.trim() && !existing.companyName?.trim()) {
    updateBody.companyName = incoming.companyName.trim();
  }
  if (incoming.jobTitle?.trim() && !existing.jobTitle?.trim()) {
    updateBody.jobTitle = incoming.jobTitle.trim();
  }
  if (incoming.address?.trim() && !existing.address?.trim()) {
    updateBody.address = incoming.address.trim();
  }

  const mergedMessage = mergeMessages(existing.message, incoming.message);
  if (mergedMessage !== (existing.message?.trim() || null)) {
    updateBody.message = mergedMessage;
  }

  if (Object.keys(updateBody).length > 0) {
    const workspaceId =
      incoming.workspaceId?.trim() || existing.workspaceId?.trim();
    if (!workspaceId) {
      throw new Error('workspaceId required');
    }
    await LeadsApi.update({
      path: { workspaceId, id: leadId },
      body: updateBody,
    });
  }

  const workspaceId =
    incoming.workspaceId?.trim() || existing.workspaceId?.trim();
  if (!workspaceId) {
    throw new Error('workspaceId required');
  }
  await addAttachments(workspaceId, leadId, incoming.attachments);
  return leadId;
}
